import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { Calculate, LockOutlined, Refresh, SchoolOutlined } from '@mui/icons-material';
import type { ChildAvailableCoursesResponse, CourseCard } from '../../services/parentCourseService';
import { parentAbacusChipLabel, resolveAbacusTileDisplay } from '../../utils/abacusTileDisplay';

interface Props {
  response: ChildAvailableCoursesResponse;
  selectedCourseIds: Set<number>;
  loading: boolean;
  childId?: number;
  onSelectionChange: (courseId: number, selected: boolean) => void;
  onContinue: () => void;
  onRetry: () => void;
  onEditProfile: () => void;
}

const COMING_SOON_CATEGORIES = [
  'Competitive Exams',
  'Art & Recreational',
  'Language & Test Prep',
  'Life Skills & Wellness',
  'Professional & STEM',
];

const AbacusTileSection: React.FC<{
  tiles: NonNullable<ChildAvailableCoursesResponse['abacusTiles']>['tiles'];
  selectedCourseIds: Set<number>;
  childId?: number;
  onSelectionChange: (courseId: number, selected: boolean) => void;
}> = ({ tiles, selectedCourseIds, childId, onSelectionChange }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Calculate fontSize="small" color="primary" />
      <Typography variant="subtitle1" fontWeight={700} color="primary">
        Skills &amp; Enrichment — Abacus Maths
      </Typography>
    </Box>
    <Divider sx={{ mb: 2 }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tiles.map((tile) => {
        const display = resolveAbacusTileDisplay(tile);
        const enrolled = tile.enrolledCourse;
        const enrollable = display === 'available';
        const teaser = tile.comingSoonTeaser;
        const chipLabel = parentAbacusChipLabel(display);
        return (
          <Paper key={tile.variant} variant="outlined" sx={{ p: 2, opacity: enrollable || enrolled ? 1 : 0.55 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: enrollable ? 1.5 : 0 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {tile.label}
              </Typography>
              <Chip label={chipLabel} size="small" color={enrollable || enrolled ? 'success' : 'default'} />
            </Box>
            {enrolled && (
              <Button
                size="small"
                variant="outlined"
                href={
                  childId
                    ? `/student-courses/${enrolled.id}?childId=${childId}`
                    : `/student-courses/${enrolled.id}`
                }
                sx={{ mt: enrolled && !enrollable ? 0 : 1 }}
              >
                {enrolled.name} — view course
              </Button>
            )}
            {teaser && !enrolled && !enrollable && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {teaser.name} — publishing soon
              </Typography>
            )}
            {enrollable &&
              tile.courses.map((course) => {
                const checked = selectedCourseIds.has(course.id);
                return (
                  <Card
                    key={course.id}
                    variant="outlined"
                    sx={{ borderColor: checked ? 'primary.main' : 'divider', mb: 1 }}
                  >
                    <CardActionArea onClick={() => onSelectionChange(course.id, !checked)}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                        <Checkbox
                          checked={checked}
                          onChange={(e) => onSelectionChange(course.id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Typography variant="body1" fontWeight={500}>
                          {course.name}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
          </Paper>
        );
      })}
    </Box>
  </Box>
);

const ComingSoonSection: React.FC<{ label: string }> = ({ label }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      opacity: 0.45,
      bgcolor: 'action.disabledBackground',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LockOutlined fontSize="small" color="disabled" />
      <Typography variant="subtitle2" color="text.disabled">
        {label}
      </Typography>
    </Box>
    <Chip label="Coming soon" size="small" disabled variant="outlined" />
  </Paper>
);

const CourseSelectionStep: React.FC<Props> = ({
  response,
  selectedCourseIds,
  loading,
  childId,
  onSelectionChange,
  onContinue,
  onRetry,
  onEditProfile,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const childName = `${response.childProfile.firstName} ${response.childProfile.lastName}`;
  const gradeLevel = response.childProfile.gradeLevel ?? '';
  const countryName = response.childProfile.countryName ?? 'Scottish';

  const schoolTypeLabel =
    response.childProfile.schoolType === 'PRIMARY'
      ? 'Primary'
      : response.childProfile.schoolType === 'SECONDARY'
        ? 'Secondary'
        : '';

  const educationSectionLabel = [countryName, schoolTypeLabel, 'School Courses']
    .filter(Boolean)
    .join(' ');

  const abacusTiles = response.abacusTiles?.tiles ?? [];
  const hasAbacusTiles = abacusTiles.length > 0;
  const hasCurriculumCourses = response.courses.length > 0;
  const selectedAbacus = abacusTiles.some((tile) =>
    (tile.courses ?? []).some((course) => selectedCourseIds.has(course.id)),
  );
  // Abacus-only selection must proceed when curriculum courses are missing or the curriculum service is down (072 US2).
  const canContinue = selectedCourseIds.size > 0 && (!response.serviceUnavailable || selectedAbacus);

  return (
    <Box>
      {/* Header row with course-code link (disabled in MVP) */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Chip
          icon={<LockOutlined fontSize="small" />}
          label="Register with a course code — coming soon"
          size="small"
          disabled
          variant="outlined"
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select one or more courses for{' '}
        <strong>{childName}</strong>
        {gradeLevel ? ` (${gradeLevel})` : ''}.
      </Typography>

      {/* ── Educational courses section ── */}
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SchoolOutlined fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          {educationSectionLabel}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {response.serviceUnavailable ? (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button size="small" startIcon={<Refresh />} onClick={onRetry} color="inherit">
              Retry
            </Button>
          }
        >
          Curriculum courses are temporarily unavailable. Other course categories below are
          still accessible.
        </Alert>
      ) : response.noCoursesFound && !hasAbacusTiles ? (
        <Box sx={{ textAlign: 'center', py: 3, mb: 3 }}>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            No curriculum courses are currently available for this student's profile.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Curriculum courses are matched to a specific year group (e.g. S1–S6 for secondary,
            P1–P7 for primary). Update the student's profile with the correct year group to see
            available courses.
          </Typography>
          <Button variant="outlined" onClick={onEditProfile}>
            Update student profile
          </Button>
        </Box>
      ) : hasCurriculumCourses ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {response.courses.map((course: CourseCard) => {
            const checked = selectedCourseIds.has(course.courseId);
            return (
              <Card
                key={course.courseId}
                variant="outlined"
                sx={{
                  borderColor: checked ? 'primary.main' : 'divider',
                  transition: 'border-color 0.2s',
                }}
              >
                <CardActionArea onClick={() => onSelectionChange(course.courseId, !checked)}>
                  <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Checkbox
                      checked={checked}
                      onChange={(e) => onSelectionChange(course.courseId, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mt: -0.5 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {course.name}
                        </Typography>
                        {course.curriculumLevel && (
                          <Chip
                            label={course.curriculumLevel}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {course.courseType && (
                          <Chip label={course.courseType} size="small" variant="outlined" />
                        )}
                      </Box>
                      {course.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {course.description}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      ) : response.profileComplete && !response.serviceUnavailable ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No curriculum courses match this profile right now. You can still enroll in Abacus Maths below
          if available.
        </Typography>
      ) : null}

      {hasAbacusTiles && (
        <AbacusTileSection
          tiles={abacusTiles}
          selectedCourseIds={selectedCourseIds}
          childId={childId}
          onSelectionChange={onSelectionChange}
        />
      )}

      {/* ── Non-educational categories — all disabled/coming-soon ── */}
      <Typography variant="subtitle1" fontWeight={700} color="text.secondary" sx={{ mb: 1, mt: 1 }}>
        Other course categories
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        {COMING_SOON_CATEGORIES.map((cat) => (
          <ComingSoonSection key={cat} label={cat} />
        ))}
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Non-educational and other course categories will be available in a future update.
      </Alert>

      {canContinue && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            disabled={selectedCourseIds.size === 0}
            onClick={onContinue}
            size="large"
          >
            Continue ({selectedCourseIds.size} selected)
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CourseSelectionStep;
